import axiosInstance from "service/axiosInstance";
import { PaginatedActivities } from "types/crm/activity";
import {
  GetCompaniesParams, Company,
  CompaniesPage,
  CompaniesResponse,
  CompanyDetailResponse,
  CreateCompanyPayload,
  CreateContactCompanyPayload,
  CreateDealCompanyPayload,
  NonAssociatedContactCompanyParams, UpdateCompanyPayload,
} from "types/crm/company";
import { toCrmPage } from "./pagination";
import { DealDetail, NonAssociatedDealParams, } from "types/crm/deal";
import { CreateCrmCompanyNotePayload, Note, NoteResponse, NotesListResponse, PaginatedNotes, UpdateCrmCompanyNotePayload } from "types/crm/notes";
import { CreateCrmCompanyTaskPayload, CrmTask, PaginatedTasks, TaskResponse, TasksListResponse, TaskStatus, TaskQuickFilter } from "types/crm/task";
import { CrmComment, CrmCommentRepliesPage } from "types/crm/comments";

/**
 * Payload for updating a company task. All fields besides the identifiers are
 * optional so callers can send a partial patch (e.g. just `status`).
 * NOTE: ideally this lives in types/crm/task.ts alongside CreateCrmCompanyTaskPayload;
 * declared here for now so nothing else has to change shape.
 */
export interface UpdateCrmCompanyTaskPayload {
  companyUuid: string;
  taskUuid: string;
  title?: string;
  description?: string;
  dueAt?: string;
  taskOwnerUuid?: string | null;
  taskType?: string;
  priority?: string;
  timezone?: string;
  status?: TaskStatus;
}

export const companyQueryKeys = {
  all: ["companies"] as const,
  lists: () => [...companyQueryKeys.all, "list"] as const,
  list: (params: GetCompaniesParams) =>
    [...companyQueryKeys.lists(), params] as const,
  infiniteLists: () => [...companyQueryKeys.all, "infinite-list"] as const,
  infiniteList: (params: Omit<GetCompaniesParams, "page">) =>
    [...companyQueryKeys.infiniteLists(), params] as const,
  resolve: (companyKey: string) =>
    [...companyQueryKeys.all, "resolve", companyKey] as const,
  detail: (companyUuid: string) =>
    [...companyQueryKeys.all, "detail", companyUuid] as const,
  byContact: (contactUuid: string) =>
    [...companyQueryKeys.all, "contact", contactUuid] as const,
  nonAssociated: (
    contactUuid: string,
    params: NonAssociatedContactCompanyParams = {},
  ) =>
    [
      ...companyQueryKeys.byContact(contactUuid),
      "non-associated",
      params,
    ] as const,
  byDeal: (dealUuid: string) =>
    [...companyQueryKeys.all, "deal", dealUuid] as const,
  nonAssociatedByDeal: (
    dealUuid: string,
    params: Omit<NonAssociatedDealParams, "dealUuid"> = {},
  ) =>
    [
      ...companyQueryKeys.byDeal(dealUuid),
      "non-associated",
      params,
    ] as const,
  notes: (companyUuid: string) =>
    [...companyQueryKeys.all, "notes", companyUuid] as const,
  tasks: (companyUuid: string, status?: TaskStatus, quickFilter?: TaskQuickFilter) =>
    [...companyQueryKeys.all, "tasks", companyUuid, status ?? "ALL", quickFilter ?? "NONE"] as const,
  activities: (companyUuid: string) =>
    [...companyQueryKeys.all, "activities", companyUuid] as const,
  noteComments: (companyUuid: string, companyNoteUuid: string) =>
    [...companyQueryKeys.all, "note-comments", companyUuid, companyNoteUuid] as const,
  noteCommentReplies: (companyUuid: string, companyNoteUuid: string, commentUuid: string, page: number) =>
    [...companyQueryKeys.noteComments(companyUuid, companyNoteUuid), "replies", commentUuid, page] as const,
  taskComments: (companyUuid: string, companyTaskUuid: string) =>
    [...companyQueryKeys.all, "task-comments", companyUuid, companyTaskUuid] as const,
  taskCommentReplies: (companyUuid: string, companyTaskUuid: string, commentUuid: string, page: number) =>
    [...companyQueryKeys.taskComments(companyUuid, companyTaskUuid), "replies", commentUuid, page] as const,

};

const dealCompanyPaths = {
  list: (dealUuid: string) => `/crm/deals/${dealUuid}/companies`,
  nonAssociated: (dealUuid: string) =>
    `/crm/deals/${dealUuid}/companies/non-associated`,
};

export const companyApi = {
  resolveCompanyUuidByKey: async (companyKey: string): Promise<string> => {
    const { data } = await axiosInstance.get(
      `/crm/companies/key/${companyKey}/uuid`,
    );
    return data.data.uuid;
  },

  // Step 2: uuid -> full detail
  getCompany: async (companyUuid: string): Promise<Company> => {
    const { data } = await axiosInstance.get<CompanyDetailResponse>(
      `/crm/companies/${companyUuid}`,
    );
    return data.data;
  },

  getCompanies: async (params: GetCompaniesParams): Promise<Company[]> => {
    const page = await companyApi.getCompaniesPage(params);
    return page.content;
  },

  /** same endpoint as getCompanies, but keeps the page envelope for infinite lists */
  getCompaniesPage: async ({
    search,
    page = 0,
    size = 20,
  }: GetCompaniesParams): Promise<CompaniesPage> => {
    const { data } = await axiosInstance.get<CompaniesResponse>("/crm/companies", {
      params: { search: search?.trim() || undefined, page, size },
    });
    return toCrmPage<Company>(data.data, page, size);
  },

  createCompany: async (payload: CreateCompanyPayload): Promise<Company> => {
    const { data } = await axiosInstance.post<CompanyDetailResponse>(
      "/crm/companies",
      payload,
    );
    return data.data;
  },

  updateCompany: async (payload: UpdateCompanyPayload): Promise<Company> => {
    const { companyUuid, ...body } = payload;
    const { data } = await axiosInstance.patch<CompanyDetailResponse>(
      `/crm/companies/${companyUuid}`,
      body,
    );
    return data.data;
  },

  // DELETE /crm/companies/{companyUuid}
  deleteCompany: async (companyUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/companies/${companyUuid}`);
  },

  getCompaniesByContact: async (contactUuid: string): Promise<Company[]> => {
    const { data } = await axiosInstance.get(
      `/crm/contacts/${contactUuid}/companies`,
    );
    return data.data?.content ?? data.data ?? [];
  },

  getNonAssociatedCompanies: async (
    contactUuid: string,
    { search, page = 0, limit = 20 }: NonAssociatedContactCompanyParams = {},
  ): Promise<CompaniesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/contacts/${contactUuid}/companies/non-associated`,
      { params: { search: search?.trim() || undefined, page, limit } },
    );
    return toCrmPage<Company>(data.data, page, limit);
  },

  linkCompanyToContact: async ({
    contactUuid,
    companyUuid,
  }: {
    contactUuid: string;
    companyUuid: string;
  }): Promise<Company> => {
    const { data } = await axiosInstance.post(
      `/crm/contacts/${contactUuid}/companies/${companyUuid}`,
    );
    return data.data;
  },

  createCompanyForContact: async ({
    contactUuid,
    ...payload
  }: CreateContactCompanyPayload & {
    contactUuid: string;
  }): Promise<Company> => {
    const { data } = await axiosInstance.post(
      `/crm/contacts/${contactUuid}/companies`,
      payload,
    );
    return data.data;
  },

  getNonAssociatedDealCompanies: async ({
    dealUuid,
    search,
    page = 0,
    limit = 20,
  }: NonAssociatedDealParams): Promise<CompaniesPage> => {
    const { data } = await axiosInstance.get(
      dealCompanyPaths.nonAssociated(dealUuid),
      { params: { search: search?.trim() || undefined, page, limit } },
    );
    return toCrmPage<Company>(data.data, page, limit);
  },

  createCompanyForDeal: async ({
    dealUuid,
    ...payload
  }: CreateDealCompanyPayload & {
    dealUuid: string;
  }): Promise<DealDetail> => {
    const { data } = await axiosInstance.post(
      dealCompanyPaths.list(dealUuid),
      payload,
    );
    return data.data;
  },

  // ---------------------------------------------------------------------------
  // Notes APIs
  // ---------------------------------------------------------------------------

  getCompanyNotes: async (
    companyUuid: string,
    page = 0,
    size = 20,
  ): Promise<PaginatedNotes> => {
    const { data } = await axiosInstance.get<NotesListResponse>(
      `/crm/companies/${companyUuid}/notes`,
      { params: { page, size } },
    );
    return data.data;
  },

  addCompanyNote: async (payload: CreateCrmCompanyNotePayload): Promise<Note> => {
    const { companyUuid, ...rest } = payload;
    const { data } = await axiosInstance.post<NoteResponse>(
      `/crm/companies/${companyUuid}/notes`,
      rest,
    );
    return data.data;
  },

  updateCompanyNote: async (payload: UpdateCrmCompanyNotePayload): Promise<CrmTask> => {
    const { companyUuid, noteUuid, ...rest } = payload;
    const { data } = await axiosInstance.put<TaskResponse>(
      `/crm/companies/${companyUuid}/notes/${noteUuid}`,
      rest,
    );
    return data.data;
  },


  deleteCompanyNote: async (companyUuid: string, noteUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/companies/${companyUuid}/notes/${noteUuid}`);
  },

  // ---------------------------------------------------------------------------
  // Task APIs
  // ---------------------------------------------------------------------------

  getCompanyTasks: async (
    companyUuid: string,
    page = 0,
    size = 20,
    status?: TaskStatus,
    quickFilter?: TaskQuickFilter,
  ): Promise<PaginatedTasks> => {
    const { data } = await axiosInstance.get<TasksListResponse>(
      `/crm/companies/${companyUuid}/tasks`,
      { params: { page, size, status: status || undefined, quickFilter } },
    );
    return data.data;
  },

  createTask: async (
    payload: CreateCrmCompanyTaskPayload,
  ): Promise<CrmTask> => {
    const { companyUuid, ...rest } = payload;
    const { data } = await axiosInstance.post<TaskResponse>(
      `/crm/companies/${companyUuid}/tasks`,
      rest,
    );
    return data.data;
  },

  updateTask: async (
    payload: UpdateCrmCompanyTaskPayload,
  ): Promise<CrmTask> => {
    const { companyUuid, taskUuid, ...rest } = payload;
    const { data } = await axiosInstance.patch<TaskResponse>(
      `/crm/companies/${companyUuid}/tasks/${taskUuid}`,
      rest,
    );
    return data.data;
  },

  deleteCompanyTask: async (
    companyUuid: string,
    taskUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(`/crm/companies/${companyUuid}/tasks/${taskUuid}`);
  },

  getCompanyActivities: async (
    companyUuid: string,
    page = 0,
    size = 20,
    tab = "ALL_ACTIVITIES",
  ): Promise<PaginatedActivities> => {
    const { data } = await axiosInstance.get<{ data: PaginatedActivities }>(
      `/crm/companies/${companyUuid}/activities`,
      { params: { tab, page, size } },
    );
    return data.data;
  },

  // ---------------------------------------------------------------------------
  // Comment APIs
  // ---------------------------------------------------------------------------
  addCompanyNoteComment: async (
    companyUuid: string,
    companyNoteUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.post(
      `/crm/companies/${companyUuid}/notes/${companyNoteUuid}/comments`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  updateCompanyNoteComment: async (
    companyUuid: string,
    companyNoteUuid: string,
    commentUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.put(
      `/crm/companies/${companyUuid}/notes/${companyNoteUuid}/comments/${commentUuid}`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  deleteCompanyNoteComment: async (
    companyUuid: string,
    companyNoteUuid: string,
    commentUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      `/crm/companies/${companyUuid}/notes/${companyNoteUuid}/comments/${commentUuid}`,
    );
  },

  getCompanyNoteCommentReplies: async (
    companyUuid: string,
    companyNoteUuid: string,
    commentUuid: string,
    page = 0,
    size = 20,
  ): Promise<CrmCommentRepliesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/companies/${companyUuid}/notes/${companyNoteUuid}/comments/${commentUuid}/replies`,
      { params: { page, size } },
    );
    return data.data;
  },

  addCompanyTaskComment: async (
    companyUuid: string,
    companyTaskUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.post(
      `/crm/companies/${companyUuid}/tasks/${companyTaskUuid}/comments`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  updateCompanyTaskComment: async (
    companyUuid: string,
    companyTaskUuid: string,
    commentUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.put(
      `/crm/companies/${companyUuid}/tasks/${companyTaskUuid}/comments/${commentUuid}`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  deleteCompanyTaskComment: async (
    companyUuid: string,
    companyTaskUuid: string,
    commentUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      `/crm/companies/${companyUuid}/tasks/${companyTaskUuid}/comments/${commentUuid}`,
    );
  },

  getCompanyTaskCommentReplies: async (
    companyUuid: string,
    companyTaskUuid: string,
    commentUuid: string,
    page = 0,
    size = 20,
  ): Promise<CrmCommentRepliesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/companies/${companyUuid}/tasks/${companyTaskUuid}/comments/${commentUuid}/replies`,
      { params: { page, size } },
    );
    return data.data;
  },

};